import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { TIERS, STATUSES, getTiers } from "../../lib/personnel";
import { useData } from "../../context/DataContext";
import api from "../../lib/api";
import { toast } from "sonner";

const EMPTY = {
  fullName: "",
  tier: "Architectural Designer",
  monthlyRate: 0,
  hourlyRate: 0,
  status: "active",
  startDate: "",
  annualLeaveEntitlement: 20,
  notes: "",
};

export default function AddPersonDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const { rawData } = useData();
  const allTiers = getTiers(rawData);
  const [busy, setBusy] = useState(false);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.fullName.trim()) {
      toast.error("Full name required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/personnel", form);
      toast.success(`Added ${form.fullName}`);
      setForm(EMPTY);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add person");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="add-person-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracked text-base">Add person</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 mt-3">
          <Field label="Full name" className="col-span-2">
            <input className={inp} value={form.fullName} onChange={(e) => update({ fullName: e.target.value })} data-testid="add-fullname" autoFocus />
          </Field>
          <Field label="Tier">
            <select className={inp} value={form.tier} onChange={(e) => update({ tier: e.target.value })} data-testid="add-tier">
              {allTiers.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select className={inp} value={form.status} onChange={(e) => update({ status: e.target.value })} data-testid="add-status">
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Monthly rate (charge-out)">
            <input type="number" className={`${inp} font-mono-num text-right`} value={form.monthlyRate} onChange={(e) => update({ monthlyRate: Number(e.target.value) || 0 })} data-testid="add-monthly" />
          </Field>
          <Field label="Hourly rate (charge-out)">
            <input type="number" step="0.01" className={`${inp} font-mono-num text-right`} value={form.hourlyRate} onChange={(e) => update({ hourlyRate: Number(e.target.value) || 0 })} data-testid="add-hourly" />
          </Field>
          <Field label="Start date">
            <input type="date" className={inp} value={form.startDate} onChange={(e) => update({ startDate: e.target.value })} />
          </Field>
          <Field label="Annual leave (days/yr)" className="col-span-2">
            <input type="number" className={`${inp} font-mono-num`} value={form.annualLeaveEntitlement} onChange={(e) => update({ annualLeaveEntitlement: Number(e.target.value) || 0 })} />
          </Field>
          <Field label="Notes (admin-only)" className="col-span-2">
            <textarea className={`${inp} h-20`} value={form.notes} onChange={(e) => update({ notes: e.target.value })} placeholder="Optional — context, rate review reminders, etc." />
          </Field>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase font-display tracked-tight text-[var(--soft-fg)] hover:text-[var(--ink)]">Cancel</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 bg-[var(--ink)] text-white text-xs uppercase font-display tracked-tight disabled:opacity-50" data-testid="add-person-save">
            {busy ? "Saving…" : "Add person"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const inp = "w-full px-3 py-2 border border-[var(--rule)] text-sm bg-white focus:border-[var(--ink)] outline-none";

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="eyebrow">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
